<script setup>
import { twMerge } from "tailwind-merge";
import WrAccelerate from "~/components/atoms/icons/WrAccelerate.vue";
import WrAggresive from "~/components/atoms/icons/WrAggresive.vue";
import WrApiDev from "~/components/atoms/icons/WrApiDev.vue";
import WrEmpGroup from "~/components/atoms/icons/WrEmpGroup.vue";
import WrLegal from "~/components/atoms/icons/WrLegal.vue";
import WrMVPDev from "~/components/atoms/icons/WrMVPDev.vue";
import WrOperational from "~/components/atoms/icons/WrOperational.vue";
import WrSerach from "~/components/atoms/icons/WrSerach.vue";
import { useGlobalStore } from "~/stores/globalStore";
import { ref, onMounted, onBeforeUnmount } from "vue";

import data from "../pages/case-studies/index-content.json";

const globalStore = useGlobalStore();
useHead({
  title:
    "Staff Augmentation That Feels Like In-House Collaboration | WebReinvent",
  meta: [
    {
      name: "description",
      content:
        "Close the gap between development and operations. WebReinvent’s expert engineers help you streamline development, boost collaboration, and ship faster—without the bottlenecks.",
    },
    {
      name: "keywords",
      content:
        "staff augmentation, software development team, hire developers, in-house collaboration, devops team, software engineers, remote developers, WebReinvent",
    },
  ],
});

const checked = ref(false);
const scrollContainer = ref(null);

let isDown = false;
let startX;
let scrollLeft;

onMounted(() => {
  const slider = scrollContainer.value;

  const mouseDownHandler = (e) => {
    isDown = true;
    slider.classList.add("cursor-grabbing");
    startX = e.pageX - slider.offsetLeft;
    scrollLeft = slider.scrollLeft;
  };

  const mouseLeaveHandler = () => {
    isDown = false;
    slider.classList.remove("cursor-grabbing");
  };

  const mouseUpHandler = () => {
    isDown = false;
    slider.classList.remove("cursor-grabbing");
  };

  const mouseMoveHandler = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - slider.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll-fast factor
    slider.scrollLeft = scrollLeft - walk;
  };

  slider.addEventListener("mousedown", mouseDownHandler);
  slider.addEventListener("mouseleave", mouseLeaveHandler);
  slider.addEventListener("mouseup", mouseUpHandler);
  slider.addEventListener("mousemove", mouseMoveHandler);

  // Cleanup
  onBeforeUnmount(() => {
    slider.removeEventListener("mousedown", mouseDownHandler);
    slider.removeEventListener("mouseleave", mouseLeaveHandler);
    slider.removeEventListener("mouseup", mouseUpHandler);
    slider.removeEventListener("mousemove", mouseMoveHandler);
  });
});

const tabs = ref([
  {
    title: "Based on Skills",
    content: [
      {
        techStack: "Back End",
        technologies: [
          {
            name: "C++",
            height: 25,
            width: 22,
            iconPosition: "bg-[position:24px_0px]",
          },
          {
            name: "Nest JS",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:52px_0px]",
          },
          {
            name: "Flask",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:82px_0px]",
          },
          {
            name: "Django",
            width: 25,
            height: 24,
            iconPosition: "bg-[position:112px_0px]",
          },
          {
            name: "Express.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:144px_0px]",
          },
          {
            name: ".NET",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:176px_0px]",
          },
          {
            name: "PHP",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:211px_0px]",
          },
          {
            name: "Ruby",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:244px_0px]",
          },
          {
            name: "Java Spring",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:270px_0px]",
          },
          {
            name: "Python",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:-4px_0px]",
          },
        ],
      },
      {
        techStack: "Front End",
        technologies: [
          {
            name: "OpenCV",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:24px_-26px]",
          },
          {
            name: "Next JS",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:50px_-26px]",
          },
          {
            name: "Svelte",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:80px_-26px]",
          },
          {
            name: "Vue.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:106px_-26px]",
          },
          {
            name: "Angular",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:140px_-26px]",
          },
          {
            name: "React.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:172px_-26px]",
          },
          {
            name: "PWA",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:208px_-26px]",
          },
          {
            name: "TypeScript",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:236px_-26px]",
          },
          {
            name: "Java Script",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:264px_-26px]",
          },
          {
            name: "HTML/CSS",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:297px_-26px]",
          },
        ],
      },
      {
        techStack: "DevOps & Cloud",
        technologies: [
          {
            name: "Conan",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:22px_-50px]",
          },
          {
            name: "Cmake",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:48px_-50px]",
          },
          {
            name: "Github Actions",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:74px_-50px]",
          },
          {
            name: "CUDA",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:102px_-50px]",
          },
          {
            name: "Datadog",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:132px_-50px]",
          },
          {
            name: "Grafana",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:160px_-50px]",
          },
          {
            name: "Kubernetes",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:186px_-50px]",
          },
          {
            name: "Podman",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:212px_-50px]",
          },
          {
            name: "Docker",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:242px_-50px]",
          },
          {
            name: "Google Cloud",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:276px_-50px]",
          },
        ],
      },
      {
        techStack: "Database Development",
        technologies: [
          {
            name: "MariaDB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:26px_-74px]",
          },
          {
            name: "Redis",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:62px_-74px]",
          },
          {
            name: "Cassandra",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:94px_-74px]",
          },
          {
            name: "MongoDB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:126px_-74px]",
          },
          {
            name: "Oracle DB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:158px_-74px]",
          },
          {
            name: "SQL Server",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:184px_-74px]",
          },
          {
            name: "Postgre SQL",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:216px_-74px]",
          },
          {
            name: "Elasticsearch",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:248px_-74px]",
          },
          {
            name: "MySQL",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:278px_-74px]",
          },
        ],
      },
      {
        techStack: "Web 3",
        technologies: [
          {
            name: "EVM",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:24px_-98px]",
          },
          {
            name: "Arbitrum",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:48px_-98px]",
          },
          {
            name: "NOWNodes",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:80px_-98px]",
          },
          {
            name: "Web3.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:114px_-98px]",
          },
          {
            name: "Hardhat",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:144px_-98px]",
          },
          {
            name: "Ethers.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:178px_-98px]",
          },
          {
            name: "OpenZeppelin",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:206px_-98px]",
          },
          {
            name: "Chainlink",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:230px_-98px]",
          },
          {
            name: "Truffle",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:258px_-98px]",
          },
          {
            name: "Moralis",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:286px_-98px]",
          },
        ],
      },
      {
        techStack: "Artificial Intelligence",
        technologies: [
          {
            name: "DL4J",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:26px_-124px]",
          },
          {
            name: "Chainer",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:64px_-126px]",
          },
          {
            name: "OpenCV",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:94px_-126px]",
          },
          {
            name: "CNTK",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:136px_-126px]",
          },
          {
            name: "Theano",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:176px_-126px]",
          },
          {
            name: "Caffe",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:220px_-128px]",
          },
        ],
      },
    ],
    value: "0",
  },
  {
    title: "Based on Role",
    content: [
      {
        techStack: "DevOps & Cloud",
        technologies: [
          {
            name: "Conan",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:22px_-50px]",
          },
          {
            name: "Cmake",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:48px_-50px]",
          },
          {
            name: "Github Actions",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:74px_-50px]",
          },
          {
            name: "CUDA",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:102px_-50px]",
          },
          {
            name: "Datadog",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:132px_-50px]",
          },
          {
            name: "Grafana",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:160px_-50px]",
          },
          {
            name: "Kubernetes",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:186px_-50px]",
          },
          {
            name: "Podman",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:212px_-50px]",
          },
          {
            name: "Docker",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:242px_-50px]",
          },
          {
            name: "Google Cloud",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:276px_-50px]",
          },
        ],
      },
      {
        techStack: "Database Development",
        technologies: [
          {
            name: "MariaDB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:26px_-74px]",
          },
          {
            name: "Redis",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:62px_-74px]",
          },
          {
            name: "Cassandra",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:94px_-74px]",
          },
          {
            name: "MongoDB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:126px_-74px]",
          },
          {
            name: "Oracle DB",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:158px_-74px]",
          },
          {
            name: "SQL Server",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:184px_-74px]",
          },
          {
            name: "Postgre SQL",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:216px_-74px]",
          },
          {
            name: "Elasticsearch",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:248px_-74px]",
          },
          {
            name: "MySQL",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:278px_-74px]",
          },
        ],
      },
      {
        techStack: "Web 3",
        technologies: [
          {
            name: "EVM",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:24px_-98px]",
          },
          {
            name: "Arbitrum",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:48px_-98px]",
          },
          {
            name: "NOWNodes",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:80px_-98px]",
          },
          {
            name: "Web3.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:114px_-98px]",
          },
          {
            name: "Hardhat",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:144px_-98px]",
          },
          {
            name: "Ethers.js",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:178px_-98px]",
          },
          {
            name: "OpenZeppelin",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:206px_-98px]",
          },
          {
            name: "Chainlink",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:230px_-98px]",
          },
          {
            name: "Truffle",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:258px_-98px]",
          },
          {
            name: "Moralis",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:286px_-98px]",
          },
        ],
      },
      {
        techStack: "Artificial Intelligence",
        technologies: [
          {
            name: "DL4J",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:26px_-124px]",
          },
          {
            name: "Chainer",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:64px_-126px]",
          },
          {
            name: "OpenCV",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:94px_-126px]",
          },
          {
            name: "CNTK",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:136px_-126px]",
          },
          {
            name: "Theano",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:176px_-126px]",
          },
          {
            name: "Caffe",
            height: 24,
            width: 25,
            iconPosition: "bg-[position:220px_-128px]",
          },
        ],
      },
    ],
    value: "1",
  },
]);

const accordionContent = ref([
  {
    title: "Why is staff augmentation important?",
    description:
      "Businesses are increasingly turning to augmented teams to help them keep up with market changes and fill talent gaps. Unlike traditional hiring, augmented hiring allows companies to stay flexible while cutting operational costs. Also, business owners have more control over their hires who join their on-site staff.",
  },
  {
    title:
      "Will your team be able to work according to my time zone preference?",
    description:
      "We can certainly accommodate your schedule. Our development and project management teams are available to you at any time (PST/MST/CST/EST), and we will communicate according to your convenience.",
  },
  {
    title: "What is the difference between outsourcing and staff augmentation?",
    description:
      "<ul><li><strong>Staff augmentation</strong> - The benefits of augmented teams include the fact that you are hiring extra IT resources to work with your team, rather than sharing the associated risks of a possible negative outcome.</li><li><strong>Outsourcing</strong> - Under the outsourcing model, you assign outsourced developers for a specific outcome, while sharing risks with your outsourcing vendor. You also have less control over their work under this model.</li></ul>",
  },
  {
    title:
      "What is the level of experience that your software developers have?",
    description:
      "WebReinvent is the software outsourcing company that is right for you! Our recruiting professionals will find you a software developer with any level of experience and skillset at a reasonable price. It doesn't matter if you need a full-stack developer or someone who specializes in a specific field.",
  },
  {
    title: "Why should you choose WebReinvent for IT staff augmentation?",
    description:
      "Companies are losing millions of dollars every month because they lack qualified IT talent. Our staff augmentation services can help you solve complicated IT talent-hiring problems. We can swiftly help your company reach its goals and keep your central systems operating at maximum capacity.",
  },
]);

const extendableAccordionContent = ref([
  {
    title: "Add Extra Force in Your Existing Project",
    description:
      "Augment your existing team with the extra talent to deliver a high-quality project within your timeline. Leverage our IT team augmentation services to build custom teams with a diverse set of skills to cover your business needs.",
  },
  {
    title: "Vendor Transition",
    description:
      "Looking for a new technology provider to launch your solution? We assist you in a smooth and effective vendor transition and take over your project in the middle of your journey.",
  },
  {
    title: "Short-Term Staff Augmentation",
    description:
      "Leverage this hiring model for urgent hiring of tech talent. Along with ready-to-go teams, we have a candidate base of top talent that can take on the job within a week.",
  },
  {
    title: "Long-Term Extended Team",
    description:
      "We offer custom recruitment for stable cooperation – you get a developer hired with the required hard and soft skills. You or your project manager can supervise task completion, while we shoulder the rest.",
  },
]);

const content = ref([
  {
    svg: markRaw(WrAccelerate),
    title: "Accelerate Time to Market",
    description: "Automate CI/CD with DevOps Talent for Faster Delivery",
    link: "/services/development-services/product-engineering",
  },
  {
    svg: markRaw(WrSerach),
    title: "Skip recruitment delays",
    description: "Skip the hiring hassle. Get the right team, fast.",
    link: "#",
  },
  {
    svg: markRaw(WrOperational),
    title: "Lower Operational Costs",
    description: "Eliminate overhead. Focus your spend on impact.",
    link: "#",
  },
  {
    svg: markRaw(WrLegal),
    title: "Fewer Legal Hassles",
    description: "We stay the official employer and handle all legalities.",
    link: "#",
  },
  {
    svg: markRaw(WrEmpGroup),
    title: "Access to Vetted Talent",
    description: "Access top tech talent fast—at a cost that works.",
    link: "#",
  },
  {
    svg: markRaw(WrAggresive),
    title: "Aggressive Deadlines",
    description: "Beat tight deadlines with our dedicated developers.",
    link: "#",
  },
  {
    svg: markRaw(WrApiDev),
    title: "Full Control",
    description: "Handpick your team and stay in control throughout.",
    link: "#",
  },
  {
    svg: markRaw(WrMVPDev),
    title: "Backup at Backend",
    description: "We’ve got your back with smart monitoring.",
    link: "#",
  },
]);


const selectedRole = ref();
const role = ref([
  { name: "New York", code: "NY" },
  { name: "Rome", code: "RM" },
  { name: "London", code: "LDN" },
  { name: "Istanbul", code: "IST" },
  { name: "Paris", code: "PRS" },
]);

const selectedLevel = ref();
const level = ref([
  { name: "New York", code: "NY" },
  { name: "Rome", code: "RM" },
  { name: "London", code: "LDN" },
  { name: "Istanbul", code: "IST" },
  { name: "Paris", code: "PRS" },
]);

const selectedContract = ref();
const contract = ref([
  { name: "New York", code: "NY" },
  { name: "Rome", code: "RM" },
  { name: "London", code: "LDN" },
  { name: "Istanbul", code: "IST" },
  { name: "Paris", code: "PRS" },
]);

const productContent = data.case_studies.imagecardcontent;

const aboutWRCardsData = ref([
  {
    id: "1",
    heading: "Motivated<br /> Developers",
    paragraph:
      "Get skilled developers who integrate seamlessly into your project—without direct employment.",
    appendClass: "bg-[rgba(0,68,250,1)]",
    appendBorder:
      "bg-[linear-gradient(135.45deg,#7EA0FE_15.59%,#022685_96.92%)]",
    paragraphColor: "text-[#E2F7FF]",
    appendImgClass: "-translate-x-1/2 left-1/2",
  },
  {
    id: "2",
    heading: "Full<br /> Support",
    paragraph:
      "Driven by passion, powered by purpose. At WebReinvent, our team thrives because they truly love what they do.",
    appendClass:
      "bg-[linear-gradient(145.88deg,#0DA4E0_10.89%,#088CBF_87.05%)]",
    appendBorder:
      "bg-[linear-gradient(135.45deg,#8ADAFA_15.59%,#046E98_96.92%)]",
    paragraphColor: "text-[#E2F7FF]",
    appendImgClass: "-translate-y-1/2 top-[35%] left-[5%]",
  },
  {
    id: "3",
    heading: "The Cooperation<br /> Of Any Length",
    paragraph:
      "Our success is rooted in commitment and the lasting trust we earn from our clients.",
    appendClass:
      "bg-[linear-gradient(140.57deg,#4C00AA_10.98%,#3B1065_93.74%)]",
    appendBorder:
      "bg-[linear-gradient(135.45deg,#6924BE_15.59%,#52188A_96.92%)]",
    paragraphColor: "text-[#E2F7FF]",
    appendImgClass: "-translate-x-1/2 top-[22%] left-1/2",
  },
  {
    id: "4",
    heading: "Direct <br /> Communication",
    paragraph:
      "We offer genuine advice, transparent processes, and fair pricing—every step of the way.",
    appendClass:
      "bg-[linear-gradient(140.57deg,#9A00FA_10.98%,#56057C_93.74%)]",
    appendBorder:
      "bg-[linear-gradient(135.45deg,#C569FF_15.59%,#580088_96.92%)]",
    paragraphColor: "text-[#E2F7FF]",
    appendImgClass: "-translate-x-1/2 top-[16%] left-1/2",
  },
  {
    id: "5",
    heading: "Simple <br /> Extension",
    paragraph:
      "Start with short-term hires and scale your remote team as needed—then scale down when the project ends.",
    appendClass:
      "bg-[linear-gradient(140.57deg,_#2600FA_10.98%,_#05097C_93.74%)]",
    appendBorder:
      "bg-[linear-gradient(135.45deg,_#6970FF_15.59%,_#002288_96.92%)]",
    paragraphColor: "text-[#E2F7FF]",
    appendImgClass: "-translate-x-1/2 top-[16%] left-1/2",
  },
]);

const augmentedTeam = ref([
  {
    designation: "Web Developers",
    description:
      "Extend your IT department with skilled and experienced programmers of any tech stack.",
    img: "/images/kamal.webp",
  },
  {
    designation: "Designers",
    description:
      "Get temporary or permanent assistance from our user experience and user interface experts.",
    img: "/images/div.webp",
  },
  {
    designation: "Software Testers",
    description:
      "Need to check quality of your app urgently? Augment your staff with a few QA engineers.",
    img: "/images/satya.webp",
  },
  {
    designation: "DevOps Engineers",
    description:
      "Reduce workload on your development team by using best DevOps practices.",
    img: "/images/chintan.webp",
  },
  {
    designation: "Web Developers",
    description:
      "Get your mobile application done in time and budget with our Android and iOS developers",
    img: "/images/sudhanshu.webp",
  },
]);

const augmentationBenefits = ref([
  "Reasonable Cost",
  "Flexible Team",
  "No long term contract",
  "Add or remove on demand",
]);
const visible = ref(false);
const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};
const handleButtonClick = () => {
  openModal();
};
</script>

<template>
  <div
    class="z-10 relative dark:bg-[linear-gradient(109.63deg,#190226_3.67%,#1F0035_83.91%)]"
  >
    <div
      class="dark:bg-[linear-gradient(270deg,_rgba(0,0,0,0.4)_-2.87%,_rgba(0,0,0,0)_51.3%,_rgba(0,0,0,0.4)_100.87%)] fixed inset-0"
    ></div>
    <section
      class="relative z-10 py-10 max-sm:pt-0 overflow-visible grid place-items-center"
    >
      <div class="max-w-screen-xl mx-auto px-4 w-full relative">
        <div class="flex max-lg:flex-col items-center">
          <div class="lg:max-w-[60%]">
            <h2 class="mb-4">
              <WrGradientText
                text="Staff Augmentation That Feels"
                append-class="pb-0 md:pb-2"
              />
              <p class="">Like In-House Collaboration</p>
            </h2>
            <p class="md:text-2xl tracking-[0.5px] lg:pr-24 mb-6">
              Close the gap between development and operations. WebReinvent’s
              expert engineers help you streamline development, boost
              collaboration, and ship faster—without the bottlenecks.
            </p>
            <WrButton @click="handleButtonClick">Get a Free Quote</WrButton>
          </div>
          <div>
            <NuxtImg
              :src="
                $colorMode.preference== 'dark'
                  ? '/images/team-dark.webp'
                  : '/images/team.webp'
              "
              alt="staff-augmentation"
              width="583"
              height="528"
              loading="eager"
              format="webp"
            />
          </div>
        </div>
      </div>
    </section>

    <WrProjectSection />

    <section
      class="relative z-10 py-10 lg:py-24 overflow-hidden grid place-items-center bg-white dark:bg-transparent"
    >
      <div
        v-if="$colorMode.preference != 'dark'"
        class="size-[372px] rounded-full bg-[#FFEFFE]/50 blur-[50px] absolute -left-36 -top-40"
      ></div>
      <div class="max-w-screen-xl mx-auto px-4 w-full">
        <h2 class="lg:text-center mb-16">
          <WrGradientText
            text="The Key Benefits of IT Staff Augmentation"
            append-class="pb-2"
          />
          <p class="text-lg mt-2.5">
            Let’s break down why businesses rely on staff augmentation to stay
            competitive and innovate faster.
          </p>
        </h2>
        <div class="relative">
          <div
            class="grid max-sm:max-w-[350px] mx-auto max-sm:place-content-left sm:grid-cols-2 lg:grid-cols-4 relative"
          >
            <LazyWrTailored
              :content="content"
              variant="secondary"
              heading-class="text-[#613FAA] dark:text-[var(--foreground)] text-xl font-medium"
            />
          </div>

          <WrButton
            container-class="mx-auto mt-6 z-10 relative"
            @click="handleButtonClick"
            >Get a Free Quote</WrButton
          >
        </div>
      </div>
    </section>

    <section
      class="relative z-10 py-10 lg:py-24 overflow-hidden grid place-items-center bg-[#EFEFEF] dark:bg-transparent dark:bg-[linear-gradient(119.57deg,_#24156D_10.08%,_#380E53_93.81%)]"
    >
      <div class="max-w-screen-xl mx-auto px-4 lg:px-10 w-full">
        <NuxtImg
          :src="
            $colorMode.preference== 'dark'
              ? '/images/staff-back-dark.webp'
              : '/images/staff-back.webp'
          "
          width="1735"
          height="889"
          loading="lazy"
          class="max-w-none mx-auto absolute top-32 -left-56"
          alt="staff-augmentation-background"
          format="webp"
        />
        <h2 class="lg:text-center text-3xl lg:text-[48px] mb-4">
          Our IT Staff Augmentation Process
        </h2>
        <p
          class="text-lg tracking-[0.5px] max-w-[710px] mx-auto lg:text-center"
        >
          At WebReinvent Software, we provide skilled remote professionals to
          boost your development team. Our experts integrate seamlessly, helping
          you meet deadlines, manage workloads, and build better
          software—faster.
        </p>
        <div class="relative mt-14">
          <WrAugmentationProcess />
          <WrButton container-class="mx-auto mt-14" @click="handleButtonClick"
            >Book a Consultation</WrButton
          >
        </div>
      </div>
    </section>

    <section class="relative bg-[#f4f4f4] dark:bg-transparent">
      <div
        class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 py-10 lg:py-24 max-md:gap-8"
      >
        <div
          class="dark:hidden size-[176px] bg-[#FFB9FC80] blur-[70px] absolute top-40 left-[45%] -translate-x-1/2 rounded-full"
        ></div>
        <div class="mx-auto max-w-[490px]">
          <h2>
            <WrGradientText
              text="IT staff augmentation"
              append-class="py-0 md:py-4"
            />
            <p>services we provide</p>
          </h2>
          <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
            As a staff augmentation firm, WebReinvent offers flexibility in
            matters of hiring our professionals. They can work with different
            contracts: see how we cooperate in more detail.
          </p>
        </div>
        <div class="grow">
          <WrAccordion variant-two :Content="extendableAccordionContent" />
        </div>
      </div>
      <div class="max-w-screen-xl mx-auto px-4 w-full py-10 lg:py-24 relative">
        <div
          v-if="$colorMode.preference != 'dark'"
          class="w-[726px] h-[332px] rounded-[100%] blur-[100px] absolute top-6 bg-white left-1/2 -translate-x-1/2"
        ></div>
        <div class="z-10 relative">
          <h2 class="text-center">
            <WrGradientText
              text="Technologies We Use"
              append-class="py-0 md:py-4"
            />
          </h2>
          <p class="text-lg text-center tracking-[0.5px]">
            Leverage advanced tools, streamlined workflows, and scalable
            infrastructure with our seasoned DevOps professionals.
          </p>
          <Tabs value="0" class="my-4 md:my-7">
            <TabList
              unstyled
              :pt="{
                tabList: {
                  class:
                    '*:selected_tab dark:aria-selected:*:text-white aria-selected:*:!text-[#501d71] dark:aria-selected:*:!text-white *:!text-[#838385] dark:*:!text-[#6950AA] flex justify-center bg-transparent border-b-0',
                },
                activeBar: { class: 'hidden' },
              }"
            >
              <Tab
                unstyled
                v-for="tab in tabs"
                :key="tab.title"
                :value="tab.value"
              >
                {{ tab.title }}
              </Tab>
            </TabList>

            <TabPanels unstyled class="bg-transparent pt-8">
              <TabPanel
                v-for="tab in tabs"
                :key="tab.value"
                :value="tab.value"
                unstyled
              >
                <div class="grid lg:grid-cols-2 gap-4 lg:gap-7">
                  <div
                    v-for="(product, index) in tab.content"
                    :key="index"
                    class=""
                  >
                    <p class="mb-3 text-lg dark:text-[#9F86DF]">
                      {{ product.techStack }}
                    </p>
                    <Tag
                      unstyled
                      v-for="(technology, index) in product.technologies"
                      :key="index"
                      :class="
                        twMerge(
                          `px-2 py-1 inline-flex  gap-1.5 items-center mr-3.5 mb-4 font-normal text-[var(--foreground)] bg-white dark:bg-[#471468] text-base rounded-full`
                        )
                      "
                    >
                      <!-- <NuxtImg
                          :src="technology.icon"
                          :width="technology.width"
                          :height="technology.height"
                          :alt="technology.name"
                          class=""
                        /> -->
                      <span
                        :class="[
                          'size-6 bg-[url(/images/sprite-img.webp)]',
                          technology.iconPosition,
                        ]"
                      ></span>
                      <span>{{ technology.name }}</span>
                    </Tag>
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      </div>
    </section>

    <section class="bg-[#EAEAEA] dark:bg-transparent">
      <div class="max-w-screen-xl mx-auto px-4 w-full py-10 lg:py-24">
        <h2 class="text-center">
          <WrGradientText
            text="Seamless Augmented Teams, Ready to Scale"
            append-class="py-0 md:py-4"
          />
        </h2>
        <p class="text-center text-lg tracking-[0.5px]">
          What a typical software development team consists of
        </p>
        <div
          ref="scrollContainer"
          class="flex overflow-x-auto no-scrollbar pt-10 px-2"
        >
          <WrCardAugmented
            :id="'0' + (index + 1)"
            :designation="item.designation"
            :description="item.description"
            :img="item.img"
            v-for="(item, index) in augmentedTeam"
          />
        </div>
        <WrButton
          @click="handleButtonClick"
          container-class="mx-auto mt-10 z-30 relative"
          >Get a Free Quote</WrButton
        >
      </div>
      <div class="max-w-screen-xl mx-auto w-full py-10 lg:pb-24 relative">
        <div
          v-if="$colorMode.preference != 'dark'"
          class="size-[304px] rounded-full bg-[linear-gradient(180deg,_rgba(255,255,255,0.8)_0%,_#F3F3F3_100%)] absolute bottom-0 -left-44"
        ></div>
        <h2 class="text-center px-4">
          <WrGradientText
            text="Why Choose WebReinvent"
            append-class="py-0 md:py-4"
          />
        </h2>
        <p
          class="text-center mx-auto max-w-[600px] text-lg mt-2 px-4 tracking-[0.5px]"
        >
          Trusted expertise, flexible engagement, and developers who feel like
          part of your team from day one.
        </p>

        <div class="mt-10 flex gap-3 overflow-x-auto no-scrollbar px-4">
          <WrCardsData :cards="aboutWRCardsData" />
        </div>
      </div>
    </section>

    <section class="bg-[#e4e4e4] dark:bg-[#270042] z-10 relative">
      <div class="max-w-screen-xl mx-auto grid md:grid-cols-2 py-16 px-4 gap-4">
        <div class="text-3xl md:text-5xl">
          Looking to <br />
          Hire Extra Force?
        </div>
        <div>
          <p class="text-lg tracking-[0.5px] mb-6">
            Trust <span class="font-semibold">WebReinvent</span> with your next
            product—we blend strategic business insight with clean, secure code,
            robust architecture, intuitive UI/UX, and complete customization
            tailored to your business or your most demanding clients.
          </p>
          <WrButton @click="handleButtonClick">Put the Team Together</WrButton>
        </div>
      </div>
    </section>

    <section class="relative">
      <div class="max-w-screen-xl mx-auto w-full py-10">
        <h2 class="text-center px-4">
          <WrGradientText text="Our Latest Works" append-class="py-0 md:py-4" />
        </h2>
        <p
          class="text-center mx-auto max-w-[600px] px-4 text-lg mt-2 tracking-[0.5px]"
        >
          Over 200 featured partners and clients
        </p>
        <div class="lg:grid lg:grid-cols-3 gap-4 max-lg:flex overflow-x-auto no-scrollbar px-4 mt-6">
          <WrProjectCard
            v-for="(item, index) in productContent"
            :key="index"
            :content="item"
            no-shadow
          />
        </div>
      </div>
    </section>

    <section class="relative">
      <div class="max-w-screen-xl mx-auto py-10 lg:pb-24">
        <h2 class="text-center px-4">
          <WrGradientText text="Trusted for excellence and efficiency" />
        </h2>
        <p
          class="px-4 text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
        >
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
        <!-- Carousel Section -->

        <WrTestimonialCardCarousel />

        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2"
          >View All</WrButton
        >
      </div>
      <div
        class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10 pb-10 lg:pb-24"
      >
        <div
          v-if="$colorMode.preference != 'dark'"
          class="size-[322px] absolute top-40 left-10 rounded-full blur-[150px] bg-[linear-gradient(180deg,_rgba(73,2,166,0.2)_0%,_rgba(255,164,235,0.2)_100%)]"
        ></div>
        <div class="max-md:text-center mx-auto max-w-[504px] z-10 relative">
          <h3>
            <WrGradientText
              text="Get all your questions"
              append-class="py-0 md:py-4"
            />
            <span>answered here</span>
          </h3>
          <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
            Here are some frequently asked questions about Laravel and our
            process for PHP Laravel development.
          </p>
        </div>
        <div class="grow">
          <WrAccordion :Content="accordionContent" />
        </div>
      </div>
     <div class="max-w-screen-lg mx-auto px-4 pb-10 w-full relative">
      <div
        class="hidden dark:block opacity-30 size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] -right-80"
      ></div>
      <WrSelectedProject />
    </div>

      <div class="max-w-screen-xl mx-auto px-4 w-full pb-10 lg:pb-24">
        <h2 class="text-center">
          <WrGradientText text="Build your team" append-class="py-0 md:py-4" />
          <p>Compare your savings</p>
        </h2>
        <div
          class="grid grid-cols-2 md:grid-cols-4 gap-0 justify-between max-w-max mx-auto my-12"
        >
          <div
            v-for="benefit in augmentationBenefits"
            class="flex items-center md:gap-1"
          >
            <video autoplay loop muted playsinline width="34" height="34">
              <source src="/images/blue-tick.webm" type="video/webm" />
            </video>
            <p
              class="text-sm md:text-base text-[var(--color-9)] tracking-[0.5px]"
            >
              {{ benefit }}
            </p>
          </div>
        </div>
        <div class="flex max-xl:flex-col gap-3">
          <!-- form augmentation -->
          <WrFromAugmentation appendClass @formSubmitted="openModal" />

          <div
            class="bg-[image:var(--border-form-card)] rounded-xl p-px relative min-w-[350px]"
          >
            <div
              class="bg-white dark:bg-transparent dark:bg-[image:var(--bg-form-card)] rounded-xl h-full"
            >
              <div
                class="p-4 rounded-t-xl flex items-center justify-between bg-[#EFEFEF] dark:bg-transparent dark:bg-[linear-gradient(112.26deg,_rgba(60,24,117,0.7)_1.19%,_rgba(77,29,119,0.7)_90.43%)]"
              >
                <span
                  >A team of 26 <br />
                  will cost you only</span
                >
                <div>
                  <p class="text-[#AE45AA] text-sm mb-3">
                    Currency ($ USD)
                    <svg
                      class="inline"
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.264138 1.12901C0.433317 0.957613 0.662742 0.861328 0.901961 0.861328C1.14118 0.861328 1.37061 0.957613 1.53978 1.12901L6.00545 5.65453L10.4711 1.12901C10.6413 0.96247 10.8691 0.870319 11.1057 0.872402C11.3422 0.874485 11.5685 0.970635 11.7358 1.14014C11.903 1.30965 11.9979 1.53896 12 1.77867C12.002 2.01838 11.9111 2.24932 11.7468 2.42175L6.64327 7.59365C6.47409 7.76504 6.24467 7.86133 6.00545 7.86133C5.76623 7.86133 5.5368 7.76504 5.36762 7.59365L0.264138 2.42175C0.0950107 2.25031 0 2.01781 0 1.77538C0 1.53295 0.0950107 1.30045 0.264138 1.12901Z"
                        fill="currentColor"
                      />
                    </svg>
                  </p>
                  <p class="text-xl font-semibold text-right">$ 102080</p>
                </div>
              </div>
              <div class="text-sm py-3 px-5">
                <p class="mb-5">Calculate your monthly pricing.</p>
                <ul class="space-y-5">
                  <li class="flex justify-between items-center">
                    <span
                      class="dark:text-[#BB84FF] font-semibold dark:font-normal"
                      >Skilled LAMP/Laravel x 4</span
                    >
                    <span class="font-semibold text-xl">$60</span>
                  </li>
                  <li class="flex justify-between items-center">
                    <span
                      class="dark:text-[#BB84FF] font-semibold dark:font-normal"
                      >Experienced LAMP/Laravel x 9</span
                    >
                    <span class="font-semibold text-xl">$60</span>
                  </li>
                  <li class="flex justify-between items-center">
                    <span
                      class="dark:text-[#BB84FF] font-semibold dark:font-normal"
                      >Expert LAMP/Laravel x 10</span
                    >
                    <span class="font-semibold text-xl">$60</span>
                  </li>
                </ul>
                <div class="mb-4 mt-32 flex items-center gap-2">
                  <label for="notify" class="sr-only text-sm font-medium"
                    >Enable Notifications</label
                  >

                  <ToggleSwitch v-model="checked" class="" inputId="notify" />
                  Yes, I want the summary
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="max-w-screen-lg mx-auto mt-10">
      <WrResultSection />
    </section>
  </div>
  <WrContactFormPopup
    :isOpen="visible"
    @close="visible = false"
    class="z-30"
    :prefilledContent="formPrefilledData"
  />
</template>
